import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import apiClient from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { consumePostAuthRedirect } from '@/utils/postAuthRedirect';
import type { Tenant, UserRole, ApiResponse, SwitchTenantResponse } from '@/types';

/** 行业选项 */
const INDUSTRIES = [
  '电子/电器',
  '机械/设备',
  '汽车/零部件',
  '化工/材料',
  '食品/饮料',
  '纺织/服装',
  '医药/医疗',
  '金属制品',
  '塑料/橡胶',
  '贸易/批发/零售',
  '建筑/房地产',
  '软件/信息技术',
  '物流/运输',
  '能源/电力',
  '农牧业',
  '企业综合集团',
  '其他',
];

const TenantSelect: React.FC = () => {
  const navigate = useNavigate();
  const { token, setCurrentTenant, setToken, setIsPlatformAdmin, setIsChannel } = useAuthStore();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [switching, setSwitching] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', shortName: '', industry: '' });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchTenants();
  }, [token]);

  const fetchTenants = async () => {
    try {
      const res = await apiClient.get<ApiResponse<Tenant[]>>('/tenants');
      setTenants(res.data.data || []);
    } catch (err: any) {
      setError('获取租户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const res = await apiClient.post<ApiResponse<Tenant>>('/tenants', {
        name: form.name.trim(),
        shortName: form.shortName.trim(),
        industry: form.industry.trim(),
      });
      const newTenant = res.data.data;
      setTenants((prev) => [...prev, newTenant]);
      setDialogOpen(false);
      setForm({ name: '', shortName: '', industry: '' });
      // 自动选择新创建的租户
      handleSelectTenant(newTenant);
    } catch (err: any) {
      setError(err?.response?.data?.message || '创建企业失败');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectTenant = async (tenant: Tenant) => {
    setSwitching(tenant.id);
    try {
      const res = await apiClient.post<ApiResponse<SwitchTenantResponse>>(
        `/tenants/${tenant.id}/switch`
      );
      const { token: newToken, role, isPlatformAdmin, isChannel } = res.data.data;
      setToken(newToken);
      setCurrentTenant(tenant, role);
      setIsPlatformAdmin(!!isPlatformAdmin);
      setIsChannel(!!isChannel);
      // v2.0.48：登录前来自公开页（如利润自测）时，切换租户后回跳原页面；
      // 否则进入闪屏（启动页），再进首页。
      navigate(consumePostAuthRedirect() || '/splash');
    } catch (err: any) {
      setError('切换租户失败');
    } finally {
      setSwitching(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        选择企业
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {tenants.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            暂无企业，请先创建
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            创建企业
          </Button>
        </Box>
      ) : (
        <List>
          {tenants.map((tenant) => (
            <Card key={tenant.id} sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleSelectTenant(tenant)}
                disabled={switching === tenant.id}
              >
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText
                  primary={tenant.name}
                  secondary={tenant.domain ? `${tenant.domain}.ibusiness.com` : ''}
                />
                {switching === tenant.id && <CircularProgress size={20} sx={{ ml: 2 }} />}
              </ListItemButton>
            </Card>
          ))}
        </List>
      )}
      {/* 创建企业弹窗 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>创建企业</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="企业名称"
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="企业简称"
            fullWidth
            value={form.shortName}
            onChange={(e) => setForm({ ...form, shortName: e.target.value })}
          />
          <TextField
            select
            margin="dense"
            label="所属行业"
            fullWidth
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
          >
            {INDUSTRIES.map((ind) => (
              <MenuItem key={ind} value={ind}>{ind}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>取消</Button>
          <Button onClick={handleCreateTenant} variant="contained" disabled={creating || !form.name.trim()}>
            {creating ? '创建中...' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantSelect;
